import { characteristics } from '../../data/characteristics';
import { harmonicLevels } from '../../data/harmonicLevels';
import type { Score } from '../../types';
import type { HarmonicScore } from '../../types/harmonic';

const RESOURCE_STATE_IDS = [9, 10, 11, 12];
const STABILISING_STATE_ID = 8;
const BLOCKER_STATE_IDS = [7, 6, 5, 4, 3, 2, 1];

export function buildAIPrompt(
  scores: Score[],
  harmonicScores: HarmonicScore[],
  instruction: string
): string {
  const beingTraits = ['ambition', 'boldness', 'integrity', 'positive-outlook'];
  const doingTraits = ['dynamic-energy', 'sociability', 'communication', 'judgment'];
  const havingTraits = ['learning', 'creativity', 'adaptability', 'visionary-thinking'];

  const formatTraitGroup = (traitIds: string[]) => scores
    .filter(s => traitIds.includes(s.characteristicId))
    .map(score => {
      const characteristic = characteristics.find(c => c.id === score.characteristicId);
      return `  ${characteristic?.name}: ${Math.round(score.normalized)}%`;
    }).join('\n');

  const getHarmonicByZone = (levelIds: number[]) => harmonicScores
    .filter(s => levelIds.includes(s.levelId))
    .sort((a, b) => b.normalized - a.normalized)
    .map(score => {
      const level = harmonicLevels.find(l => l.id === score.levelId);
      return `  ${level?.name}: ${Math.round(score.normalized)}%`;
    }).join('\n');

  const resourceScores = harmonicScores.filter(s => RESOURCE_STATE_IDS.includes(s.levelId));
  const blockerScores = harmonicScores.filter(s => BLOCKER_STATE_IDS.includes(s.levelId));
  const stabilisingScore = harmonicScores.find(s => s.levelId === STABILISING_STATE_ID);

  const highestResource = resourceScores.reduce((prev, current) =>
    current.normalized > prev.normalized ? current : prev, resourceScores[0]);
  const highestBlocker = blockerScores.reduce((prev, current) =>
    current.normalized > prev.normalized ? current : prev, blockerScores[0]);

  const highestResourceLevel = harmonicLevels.find(l => l.id === highestResource?.levelId);
  const highestBlockerLevel = harmonicLevels.find(l => l.id === highestBlocker?.levelId);

  return `
NLP OS™ Assessment Data:

═══════════════════════════════════════
CHARACTERISTIC PROFILE
═══════════════════════════════════════

Being Traits (identity, inner qualities, mindset, values):
${formatTraitGroup(beingTraits)}

Doing Traits (behaviour, expression, action, communication):
${formatTraitGroup(doingTraits)}

Having Traits (capacities, resources, learning, adaptability, vision):
${formatTraitGroup(havingTraits)}

═══════════════════════════════════════
HARMONIC SCALE PROFILE
═══════════════════════════════════════

GROWTH / RESOURCE STATES (Interest and above):
${getHarmonicByZone(RESOURCE_STATE_IDS)}

STABILISING STATE:
  Rational Neutrality: ${stabilisingScore ? Math.round(stabilisingScore.normalized) : 0}%

BLOCKER / INTERFERENCE STATES (Antagonism and below):
${getHarmonicByZone(BLOCKER_STATE_IDS)}

═══════════════════════════════════════
KEY HARMONIC INTERPRETATION
═══════════════════════════════════════

Highest Resource State: ${highestResourceLevel?.name} — ${highestResource ? Math.round(highestResource.normalized) : 0}%
Highest Blocker State: ${highestBlockerLevel?.name} — ${highestBlocker ? Math.round(highestBlocker.normalized) : 0}%
Stabilising State: Rational Neutrality — ${stabilisingScore ? Math.round(stabilisingScore.normalized) : 0}%

Remember: Higher states show available potential. Lower states show what may block that potential. The highest blocker often explains why the person is not fully living the potential shown by their strongest traits.

═══════════════════════════════════════
INSTRUCTION
═══════════════════════════════════════

${instruction}
`;
}
